import React, { useState, useEffect } from 'react';
import { Play, X, Award, Users, GraduationCap, Building2, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import getFullUrl from '../../utils/formatUrl';

const Hero = ({ data = {} }) => {
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const navigate = useNavigate();
    const isVideo = data.bgType === 'video' && (data.video);
    const videoUrl = getFullUrl(data.video);
    const imageUrl = getFullUrl(data.image);

    useEffect(() => {
        const timer = setTimeout(() => setIsLoaded(true), 100);
        return () => clearTimeout(timer);
    }, []);

    const handleExploreClick = () => {
        navigate('/about');
    };

    const handleWatchClick = () => {
        setIsVideoModalOpen(true);
    };

    const handleScrollDown = () => {
        window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
    };

    const stats = [
        { icon: Award, value: data.years ? `${data.years}+` : '20++', label: 'Years Legacy' },
        { icon: GraduationCap, value: data.alumni || '15K+', label: 'Alumni' },
        { icon: Building2, value: data.campuses || '8+', label: 'Campuses' },
        { icon: Users, value: data.faculty || '500+', label: 'Faculty' },
    ];

    return (
        <>
            <section className="relative h-[100dvh] min-h-[500px] bg-slate-900 overflow-x-hidden flex items-center">
                {/* Animated Gradient Overlays */}
                <div className="absolute inset-0 z-10">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#001a33]/95 via-[#003366]/70 to-transparent"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"></div>
                    {/* Decorative grid pattern */}
                    <div className="absolute inset-0 opacity-[0.03]" style={{
                        backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                        backgroundSize: '60px 60px'
                    }}></div>
                </div>

                {/* Floating Decorative Elements */}
                <div className="absolute top-20 right-20 w-72 h-72 bg-[#cc0000]/10 rounded-full blur-[100px] z-10 animate-pulse"></div>
                <div className="absolute bottom-40 left-10 w-96 h-96 bg-[#003366]/20 rounded-full blur-[120px] z-10"></div>
                

                {/* Background Content */}
                {isVideo ? (
                    <video 
                        autoPlay 
                        muted 
                        loop 
                        playsInline
                        crossOrigin="anonymous"
                        className="absolute inset-0 w-full h-full object-contain md:object-cover bg-slate-900"
                        key={videoUrl}
                    >
                        <source src={videoUrl} />
                        Your browser does not support the video tag.
                    </video>
                ) : (
                    <img 
                        src={imageUrl || "https://images.unsplash.com/photo-1562774053-701939374585?q=80&w=2000&auto=format&fit=crop"}
                        className="absolute inset-0 w-full h-full object-contain md:object-cover bg-slate-900 md:animate-slowZoom md:scale-105"
                        alt="Hero Banner"
                    />
                )}

                {/* Main Content Container */}
                <div className="relative z-20 max-w-7xl ml-0 mr-auto px-6 sm:px-12 lg:px-24 xl:px-32 h-full flex flex-col justify-center pb-36 sm:pb-32 md:pb-28">
                    <div className="max-w-3xl space-y-6 md:space-y-8 text-left items-start">
                        
                        {/* Badge with animated entrance */}
                        {data.badge && (
                            <div className={`transition-all duration-700 delay-200 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                                <span className="inline-flex items-center gap-2 bg-amber-400/90 backdrop-blur-sm text-[#003366] px-5 py-2 text-[10px] font-black uppercase tracking-[5px] shadow-lg rounded-full">
                                    <span className="w-2 h-2 bg-[#003366] rounded-full animate-pulse"></span>
                                    {data.badge}
                                </span>
                            </div>
                        )}
                        
                        {/* Main Headline with staggered animation */}
                        <div className={`transition-all duration-700 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                            <p className="text-white font-black uppercase tracking-[8px] text-[10px] mb-2 drop-shadow-lg">Welcome to</p>
                            <h1 className="leading-[1] tracking-tight">
                                <div className="text-4xl sm:text-6xl md:text-7xl lg:text-6xl xl:text-8xl text-[#cc0000] mb-4 drop-shadow-sm" style={{ fontFamily: 'Forte' }}>
                                    Arya Sabhyata
                                </div>
                                <div className="text-[14px] sm:text-[18px] md:text-[22px] lg:text-[24px] xl:text-[28px] text-white uppercase tracking-[6px] sm:tracking-[8px] md:tracking-[10px] opacity-90 font-bold ml-1 font-sans">
                                    Group of Institutions
                                </div>
                            </h1>
                        </div>

                        {/* Description with glass card */}
                        <div className={`transition-all duration-700 delay-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                            <div className="relative max-w-xl">
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#cc0000] via-amber-400 to-transparent rounded-full"></div>
                                <p className="pl-6 text-[13px] sm:text-[14px] md:text-[15px] text-white/80 font-medium leading-[1.8] tracking-wide">
                                    {data.description || 'Delivering Academic Excellence and Holistic Growth. Shaping the leaders of tomorrow with quality education.'}
                                </p>
                            </div>
                        </div>

                        {/* CTA Buttons with animation */}
                        <div className={`flex flex-wrap gap-3 sm:gap-4 pt-2 md:pt-4 transition-all duration-700 delay-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                            {data.btn1Text && (
                                <button 
                                    onClick={handleExploreClick}
                                    className="group relative bg-[#cc0000] text-white px-8 sm:px-10 py-4 sm:py-5 font-black uppercase text-[11px] sm:text-[12px] tracking-[3px] transition-all duration-300 shadow-2xl shadow-red-900/30 hover:shadow-red-900/50 hover:scale-[1.02] active:scale-[0.98] cursor-pointer overflow-hidden"
                                >
                                    <span className="relative z-10">{data.btn1Text}</span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                                </button>
                            )}
                            
                            {data.btn2Text && (
                                <button 
                                    onClick={handleWatchClick}
                                    className="group bg-white/5 backdrop-blur-xl border border-white/10 hover:bg-white/15 hover:border-white/25 text-white px-6 sm:px-8 py-4 sm:py-5 font-black uppercase text-[11px] sm:text-[12px] tracking-[3px] transition-all duration-300 flex items-center gap-3 cursor-pointer"
                                >
                                    <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                                        <Play size={14} className="fill-current ml-0.5" />
                                    </span>
                                    {data.btn2Text}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Stats Bar - Bottom Glass Strip */}
                <div className={`absolute bottom-0 left-0 right-0 z-30 transition-all duration-1000 delay-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    <div className="bg-white/[0.05] backdrop-blur-xl border-t border-white/10">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="grid grid-cols-2 md:grid-cols-4">
                                {stats.map((stat, i) => (
                                    <div 
                                        key={i} 
                                        className={`flex items-center gap-3 sm:gap-4 py-5 sm:py-6 px-4 sm:px-6 ${i !== stats.length - 1 ? 'border-r border-white/5' : ''} group hover:bg-white/5 transition-colors duration-300 cursor-default`}
                                    >
                                        <stat.icon size={28} className="text-amber-400 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                        <div>
                                            <div className="text-xl sm:text-2xl md:text-3xl font-black text-white tracking-tight">{stat.value}</div>
                                            <div className="text-[11px] sm:text-[12px] md:text-[13px] font-bold uppercase text-white/70 tracking-[2px] mt-0.5">{stat.label}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scroll Down Indicator */}
                <button 
                    onClick={handleScrollDown}
                    className={`absolute bottom-28 md:bottom-24 left-1/2 -translate-x-1/2 z-30 text-white/30 hover:text-white/60 transition-all duration-300 hidden lg:flex flex-col items-center gap-2 cursor-pointer ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                >
                    <ChevronDown size={20} className="animate-bounce" />
                </button>
            </section>

            {/* Fullscreen Video Modal */}
            {isVideoModalOpen && (
                <div 
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 backdrop-blur-md"
                    onClick={() => setIsVideoModalOpen(false)}
                >
                    {/* Close Button */}
                    <button 
                        onClick={() => setIsVideoModalOpen(false)}
                        className="absolute top-6 right-6 z-[10000] bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all backdrop-blur-md border border-white/10 hover:border-white/20 hover:scale-110"
                    >
                        <X size={24} />
                    </button>

                    {/* Video Container */}
                    <div 
                        className="relative w-[95vw] sm:w-[92vw] max-w-5xl h-auto aspect-video rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10 animate-scaleIn bg-black"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {videoUrl ? (
                            <video 
                                autoPlay 
                                controls 
                                playsInline
                                crossOrigin="anonymous"
                                className="w-auto h-full max-w-full object-contain bg-black mx-auto"
                            >
                                <source src={videoUrl} />
                                Your browser does not support the video tag.
                            </video>
                        ) : (
                            <div className="w-full h-full bg-slate-900 flex flex-col items-center justify-center text-white">
                                <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-6 border border-white/10">
                                    <Play size={32} className="text-white/30 ml-1" />
                                </div>
                                <p className="text-slate-400 font-bold uppercase tracking-[4px] text-sm">No video available</p>
                                <p className="text-slate-600 text-xs mt-3 tracking-wide">Upload a video from the admin panel</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default Hero;
